import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  fetchNotifications,
  createNotification as apiCreate,
  updateNotification as apiUpdate,
  deleteNotification,
} from '../services/notificationsService.js';
import documentChunkingProgressService, { createPoller } from '../services/documentChunkingProgressService.js';
import { phDocumentIndexingFailed, phRequirementExtractionFailed, phDocumentIndexed } from '../utils/posthogEvents';

const NotificationsContext = createContext(null);

let toastIdCounter = 0;

// Jobs older than this that are still PENDING are considered dead — resolve
// them immediately without starting a poll.
const STALE_JOB_MS = 2 * 60 * 60 * 1000; // 2 hours

export function NotificationsProvider({ children, projectId }) {
  const [notifications, setNotifications] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [jobProgress, setJobProgress] = useState({});
  // Map<jobId, { folderName, documents, isDeliverableOnly, hasDrawingDeliverable }>
  // — populated only when the job is started in this session (see
  // resumePollingForJob's `meta` param). Not persisted server-side, so it's
  // unavailable after a page reload; consumers must fall back gracefully.
  const [jobMeta, setJobMeta] = useState({});
  const resumedJobsRef = useRef(new Set());
  const toastedIdsRef  = useRef(new Set());
  // Map<jobId, cancelFn> — prevents duplicate pollers for the same job
  // and enables clean cancellation on unmount.
  const activeJobsRef  = useRef(new Map());
  // Set<notificationId> — ids the user just dismissed/deleted. The backend
  // delete can still be in flight when the next 10s background refresh
  // fetches a stale list, which would otherwise resurrect the notification
  // we just removed. Filter those out until the server confirms they're
  // actually gone (see reconcileDismissed below).
  const dismissedIdsRef = useRef(new Set());
  // Set<notificationId> — EXTRACTION_FAILED notifications already captured to
  // PostHog, so a re-fetch (interval poll or reload) doesn't double-report.
  const capturedExtractionFailuresRef = useRef(new Set());

  const captureExtractionFailures = useCallback((items) => {
    items.forEach(n => {
      if (n.type !== 'EXTRACTION_FAILED') return;
      if (capturedExtractionFailuresRef.current.has(n.notificationId)) return;
      capturedExtractionFailuresRef.current.add(n.notificationId);
      phRequirementExtractionFailed({
        projectId: n.projectId,
        jobId: n.jobId,
        reason: 'stale_extraction',
        errorMessage: n.message,
      });
    });
  }, []);

  const reconcileDismissed = useCallback((rawItems) => {
    const dismissed = dismissedIdsRef.current;
    if (dismissed.size === 0) return rawItems;
    const stillPresentIds = new Set(rawItems.map(n => n.notificationId));
    dismissed.forEach(id => { if (!stillPresentIds.has(id)) dismissed.delete(id); });
    return rawItems.filter(n => !dismissed.has(n.notificationId));
  }, []);

  const addToast = useCallback(({ type, title, message, duration = 7000 }) => {
    const id = ++toastIdCounter;
    setToasts(prev => [...prev, { id, type, title, message, hiding: false }]);
    setTimeout(() => {
      setToasts(prev => prev.map(t => t.id === id ? { ...t, hiding: true } : t));
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 280);
    }, duration);
    return id;
  }, []);

  // One createPoller() instance per job — no shared state, no mutual
  // cancellation. Requirements-extraction completion is NOT handled here:
  // the requirementsExtractionWorker Lambda writes its own
  // "Requirements extraction started/complete" notifications straight to the
  // table, which the 10s fetchNotifications loop below surfaces. This poller
  // only tracks the separate document-indexing (Research) job.
  const resumePollingForJob = useCallback(({ jobId, notificationId, projectId: pId, meta }) => {
    if (meta) setJobMeta(prev => ({ ...prev, [jobId]: meta }));
    if (activeJobsRef.current.has(jobId)) return; // already polling this job

    const poller = createPoller();
    activeJobsRef.current.set(jobId, () => poller.stopPolling());

    poller.startPolling({
      jobId,
      statusUrl: process.env.REACT_APP_CHUNK_STATUS_URL,
      onUpdate: (state) => {
        setJobProgress(prev => ({
          ...prev,
          [jobId]: {
            stage: state.stage,
            status: state.status,
            progress: state.progress,
            message: state.message,
            completedDocuments: state.completedDocuments,
            totalDocuments: state.totalDocuments,
          },
        }));
      },
      onDone: async () => {
        activeJobsRef.current.delete(jobId);
        setJobProgress(prev => {
          const next = { ...prev };
          delete next[jobId];
          return next;
        });
        setJobMeta(prev => {
          const next = { ...prev };
          delete next[jobId];
          return next;
        });
        try {
          await apiUpdate(notificationId, {
            projectId: pId,
            type: 'info',
            title: 'Indexing started',
            message: 'Documents were sent for indexing.',
            status: 'COMPLETE',
          });
          setNotifications(prev =>
            prev.map(n =>
              n.notificationId === notificationId ? { ...n, status: 'COMPLETE' } : n
            )
          );

          const successNotif = await apiCreate({
            projectId: pId,
            type: 'success',
            title: 'Documents ready for research',
            message: 'All documents are indexed and searchable in Research.',
          });
          if (successNotif?.notificationId) toastedIdsRef.current.add(successNotif.notificationId);
          setNotifications(prev => [successNotif, ...prev]);

          phDocumentIndexed({
            projectId: pId,
            jobId,
            durationMs: meta?.startedAt ? Date.now() - meta.startedAt : null,
          });
        } catch (err) {
          console.error('[NotificationsContext] Failed to handle job completion:', err);
        }
      },
      onError: async (err) => {
        activeJobsRef.current.delete(jobId);
        setJobProgress(prev => {
          const next = { ...prev };
          delete next[jobId];
          return next;
        });
        setJobMeta(prev => {
          const next = { ...prev };
          delete next[jobId];
          return next;
        });

        // Only a confirmed backend FAILED stage is a real failure. A missing
        // job record (404 after grace) or a network blip just means we lost
        // the connection to the status endpoint — the job may still finish in
        // the background — so we leave the notification PENDING and let the
        // resume-on-reload logic re-attach a poller next time the app loads
        // (and the 2h stale guard auto-resolve it if it never comes back).
        // No misleading "we stopped checking" message.
        if (err?.code !== 'FAILED') return;

        phDocumentIndexingFailed({
          projectId: pId,
          jobId,
          reason: err?.reason || err?.code,
          errorMessage: err?.message,
        });

        try {
          await apiUpdate(notificationId, {
            projectId: pId,
            type: 'warning',
            title: 'Indexing may have failed',
            message: 'One or more documents may not have indexed correctly.',
            status: 'COMPLETE',
          });
          setNotifications(prev =>
            prev.map(n =>
              n.notificationId === notificationId
                ? { ...n, type: 'warning', title: 'Indexing may have failed', message: 'One or more documents may not have indexed correctly.', status: 'COMPLETE' }
                : n
            )
          );
        } catch (e) {
          console.error('[NotificationsContext] Failed to handle job failure:', e);
        }
      },
    });
  }, []);

  useEffect(() => {
    if (!projectId) return;

    let alive = true;

    async function load() {
      try {
        const items = reconcileDismissed(await fetchNotifications(projectId));
        if (!alive) return;
        setNotifications(items);
        items.forEach(n => toastedIdsRef.current.add(n.notificationId));
        captureExtractionFailures(items);

        items.forEach(notif => {
          if (notif.status !== 'PENDING' || !notif.jobId) return;
          if (resumedJobsRef.current.has(notif.jobId)) return;

          resumedJobsRef.current.add(notif.jobId);

          // Stale guard: if the job is old enough that it's certainly dead,
          // resolve it immediately without spinning up a poller.
          const createdAt = notif.createdAt ? new Date(notif.createdAt).getTime() : 0;
          if (createdAt && Date.now() - createdAt > STALE_JOB_MS) {
            apiUpdate(notif.notificationId, {
              projectId: notif.projectId,
              status: 'COMPLETE',
              type: notif.type,
              title: notif.title,
              message: notif.message,
            }).catch(err =>
              console.warn('[NotificationsContext] Failed to auto-complete stale job:', notif.jobId, err)
            );
            return;
          }

          resumePollingForJob({
            jobId: notif.jobId,
            notificationId: notif.notificationId,
            projectId: notif.projectId,
            total: null,
          });
        });
      } catch (err) {
        console.error('[NotificationsContext] Failed to load notifications:', err);
      }
    }

    load();

    const interval = setInterval(async () => {
      if (!alive) return;
      try {
        const items = reconcileDismissed(await fetchNotifications(projectId));
        if (!alive) return;
        setNotifications(items);
        captureExtractionFailures(items);
        items.forEach(n => {
          if (!toastedIdsRef.current.has(n.notificationId)) {
            toastedIdsRef.current.add(n.notificationId);
            addToast({
              type: n.type || 'info',
              title: n.title,
              message: n.message,
              duration: n.type === 'success' ? 10000 : 7000,
            });
          }
        });
      } catch (err) {
        console.error('[NotificationsContext] Polling error:', err);
      }
    }, 10000);

    return () => {
      alive = false;
      clearInterval(interval);
      // Cancel all in-flight job pollers on unmount / projectId change
      activeJobsRef.current.forEach(cancel => cancel());
      activeJobsRef.current.clear();
    };
  }, [projectId, addToast, resumePollingForJob, reconcileDismissed, captureExtractionFailures]);

  const addNotification = useCallback(async ({ type, title, message, jobId }) => {
    if (!projectId) return null;
    try {
      const notif = await apiCreate({ projectId, type, title, message, jobId });
      if (notif?.notificationId) toastedIdsRef.current.add(notif.notificationId);
      setNotifications(prev => [notif, ...prev]);
      return notif;
    } catch (err) {
      console.error('[NotificationsContext] Failed to create notification:', err);
      return null;
    }
  }, [projectId]);

  const updateNotificationById = useCallback(async (notificationId, updates) => {
    if (!projectId) return;
    try {
      await apiUpdate(notificationId, { projectId, ...updates });
      setNotifications(prev =>
        prev.map(n => n.notificationId === notificationId ? { ...n, ...updates, unread: true } : n)
      );
    } catch (err) {
      console.error('[NotificationsContext] Failed to update notification:', err);
    }
  }, [projectId]);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, hiding: true } : t));
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 280);
  }, []);

  const markAllRead = useCallback(async () => {
    if (!projectId) return;
    // Action-required notifications (e.g. zone-confirm) must survive "Mark all read" —
    // they can only be resolved by completing the action. Delete the rest individually.
    const removable = notifications.filter(n => !n.requiresAction);
    const keep      = notifications.filter(n => n.requiresAction);
    if (removable.length === 0) return;
    try {
      removable.forEach(n => dismissedIdsRef.current.add(n.notificationId));
      setNotifications(keep);
      await Promise.all(
        removable.map(n =>
          deleteNotification(n.notificationId, n.projectId).catch(err =>
            console.error('[NotificationsContext] Failed to delete notification:', err)
          )
        )
      );
    } catch (err) {
      console.error('[NotificationsContext] Failed to delete notifications:', err);
    }
  }, [projectId, notifications]);

  const dismissNotif = useCallback(async (notificationId) => {
    const notif = notifications.find(n => n.notificationId === notificationId);
    if (!notif) return;
    // Guard: action-required notifications are non-dismissible.
    if (notif.requiresAction) return;

    dismissedIdsRef.current.add(notificationId);
    setNotifications(prev => prev.filter(n => n.notificationId !== notificationId));

    try {
      await deleteNotification(notificationId, notif.projectId);
    } catch (err) {
      console.error('[NotificationsContext] Failed to delete notification:', err);
      dismissedIdsRef.current.delete(notificationId);
      setNotifications(prev => [notif, ...prev]);
    }
  }, [notifications]);

  const unreadCount = notifications.filter(n => n.unread).length;

  return (
    <NotificationsContext.Provider value={{
      notifications,
      toasts,
      unreadCount,
      jobProgress,
      jobMeta,
      addNotification,
      updateNotificationById,
      addToast,
      dismissToast,
      dismissNotif,
      markAllRead,
      resumePollingForJob,
    }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotificationsContext must be used inside NotificationsProvider');
  return ctx;
}